import React from 'react';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Radar, Line } from 'react-chartjs-2';
import { ShipIcon, AnchorIcon } from 'lucide-react';
import { ErrorBoundary } from './ErrorBoundary';
ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);
// Disable any automatic Chart.js network requests
ChartJS.defaults.font.family = "'Inter', sans-serif";
ChartJS.defaults.animation = false;
ChartJS.defaults.responsive = true;
export const Dashboard = ({
  simulationData
}) => {
  const {
    alignmentScore,
    ltvCacRatio,
    paybackPeriod,
    projectedRevenue,
    isSmoothSailor
  } = simulationData;
  const formatRevenue = value => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value}`;
  };
  // Radar chart data
  const radarData = {
    labels: ['Market-Product', 'Product-Channel', 'Channel-Model', 'Model-Market'],
    datasets: [{
      label: 'Current',
      data: [80, 85, 75, 80],
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      borderColor: 'rgba(59, 130, 246, 1)',
      borderWidth: 2,
      pointBackgroundColor: 'rgba(59, 130, 246, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(59, 130, 246, 1)'
    }]
  };
  // Line chart data for 36-month trajectory
  const generateGrowthData = () => {
    const months = Array.from({
      length: 37
    }, (_, i) => i);
    // Generate smooth growth curve
    const revenueData = months.map(month => {
      if (month === 0) return 0;
      return Math.pow(month, 1.7) * (projectedRevenue / Math.pow(36, 1.7)) * (isSmoothSailor ? 1 : 0.7);
    });
    // Generate cash flow curve (simplified)
    const cashFlowData = months.map((month, i) => {
      if (month === 0) return -500000;
      const revenue = revenueData[i];
      const costs = revenue * 0.7 + 300000;
      return revenue - costs;
    });
    return {
      labels: months,
      datasets: [{
        label: 'Monthly Revenue',
        data: revenueData,
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      }, {
        label: 'Cash Flow',
        data: cashFlowData,
        borderColor: 'rgba(16, 185, 129, 1)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      }]
    };
  };
  return <div className="bg-[#1E293B] bg-opacity-60 backdrop-blur-md rounded-xl p-6 border border-[#334155] shadow-lg" data-id="element-19">
      <div className="flex items-center justify-between mb-4" data-id="element-20">
        <div className="flex items-center" data-id="element-21">
          <div className="w-5 h-5 mr-2 text-blue-500" data-id="element-22" />
          <h2 className="text-lg font-bold text-blue-500" data-id="element-23">Live Simulation</h2>
        </div>
        <div className="flex items-center space-x-1 bg-green-500 bg-opacity-20 px-2 py-1 rounded text-xs text-green-500" data-id="element-24">
          <span data-id="element-25">Synced</span>
        </div>
      </div>
      <div className="mb-6" data-id="element-26">
        <div className="flex space-x-2 mb-4" data-id="element-27">
          <button className="bg-blue-500 text-white px-3 py-1 text-xs rounded" data-id="element-28">
            Current
          </button>
          <button className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 text-xs rounded" data-id="element-29">
            Base
          </button>
          <button className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 text-xs rounded" data-id="element-30">
            Best
          </button>
          <button className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1 text-xs rounded" data-id="element-31">
            Worst
          </button>
        </div>
        <div className="bg-[#0F172A] rounded-lg p-4 mb-6" data-id="element-32">
          <div className="flex items-center mb-3" data-id="element-33">
            {isSmoothSailor ? <>
                <div className="w-8 h-8 bg-green-500 bg-opacity-20 rounded-full flex items-center justify-center mr-3" data-id="element-34">
                  <ShipIcon className="w-4 h-4 text-green-500" data-id="element-35" />
                </div>
                <span className="text-green-500 font-semibold" data-id="element-36">
                  Smooth Sailor
                </span>
              </> : <>
                <div className="w-8 h-8 bg-red-500 bg-opacity-20 rounded-full flex items-center justify-center mr-3" data-id="element-37">
                  <AnchorIcon className="w-4 h-4 text-red-500" data-id="element-38" />
                </div>
                <span className="text-red-500 font-semibold" data-id="element-39">
                  Tugboat Struggle
                </span>
              </>}
            <div className="ml-auto text-2xl font-bold" data-id="element-40">
              {alignmentScore}% Aligned
            </div>
          </div>
          <div className="h-48 mb-4" data-id="element-41">
            <ErrorBoundary fallback={<div className="h-full flex items-center justify-center text-red-400" data-id="element-43">
                  Chart could not be loaded
                </div>} data-id="element-42">
              <Radar data={radarData} options={{
              scales: {
                r: {
                  min: 0,
                  max: 100,
                  ticks: {
                    display: false
                  },
                  pointLabels: {
                    color: 'rgba(148, 163, 184, 1)',
                    font: {
                      size: 10
                    }
                  },
                  grid: {
                    color: 'rgba(51, 65, 85, 0.5)'
                  },
                  angleLines: {
                    color: 'rgba(51, 65, 85, 0.5)'
                  }
                }
              },
              plugins: {
                legend: {
                  display: false
                }
              },
              maintainAspectRatio: false
            }} data-id="element-44" />
            </ErrorBoundary>
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-45">
            <div className="bg-[#1E293B] p-3 rounded-lg" data-id="element-46">
              <div className="text-xs text-gray-400 mb-1" data-id="element-47">LTV:CAC Ratio</div>
              <div className={`text-lg font-mono font-bold ${ltvCacRatio >= 3 ? 'text-green-500' : 'text-yellow-500'}`} data-id="element-48">
                {ltvCacRatio.toFixed(1)}:1
              </div>
            </div>
            <div className="bg-[#1E293B] p-3 rounded-lg" data-id="element-49">
              <div className="text-xs text-gray-400 mb-1" data-id="element-50">Payback Period</div>
              <div className={`text-lg font-mono font-bold ${paybackPeriod <= 12 ? 'text-green-500' : 'text-yellow-500'}`} data-id="element-51">
                {paybackPeriod.toFixed(1)} mo
              </div>
            </div>
            <div className="bg-[#1E293B] p-3 rounded-lg" data-id="element-52">
              <div className="text-xs text-gray-400 mb-1" data-id="element-53">
                Projected Revenue
              </div>
              <div className={`text-lg font-mono font-bold ${projectedRevenue >= 100000000 ? 'text-green-500' : 'text-red-500'}`} data-id="element-54">
                {formatRevenue(projectedRevenue)}
              </div>
            </div>
            <div className="bg-[#1E293B] p-3 rounded-lg" data-id="element-55">
              <div className="text-xs text-gray-400 mb-1" data-id="element-56">Channel CAC</div>
              <div className="text-lg font-mono font-bold text-white" data-id="element-57">
                $5,000
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-58">
          <h3 className="text-sm font-semibold text-gray-300 mb-2" data-id="element-59">
            36-Month Trajectory
          </h3>
          <div className="bg-[#0F172A] rounded-lg p-4 h-64" data-id="element-60">
            <ErrorBoundary fallback={<div className="h-full flex items-center justify-center text-red-400" data-id="element-62">
                  Chart could not be loaded
                </div>} data-id="element-61">
              <Line data={generateGrowthData()} options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  grid: {
                    color: 'rgba(51, 65, 85, 0.2)'
                  },
                  ticks: {
                    color: 'rgba(148, 163, 184, 0.7)',
                    maxTicksLimit: 12,
                    callback: value => value % 6 === 0 ? value : ''
                  }
                },
                y: {
                  grid: {
                    color: 'rgba(51, 65, 85, 0.2)'
                  },
                  ticks: {
                    color: 'rgba(148, 163, 184, 0.7)',
                    callback: value => formatRevenue(value)
                  }
                }
              },
              plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                  callbacks: {
                    label: context => {
                      return `${context.dataset.label}: ${formatRevenue(context.parsed.y)}`;
                    }
                  }
                }
              },
              interaction: {
                mode: 'index',
                intersect: false
              }
            }} data-id="element-63" />
            </ErrorBoundary>
          </div>
        </div>
        <div className="flex space-x-4" data-id="element-64">
          <button className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] border border-[#334155] rounded-lg py-2 px-4 text-sm flex items-center justify-center" data-id="element-65">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" data-id="element-66">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" data-id="element-67"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" data-id="element-68"></path>
            </svg>
            Copy Config
          </button>
          <button className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] border border-[#334155] rounded-lg py-2 px-4 text-sm flex items-center justify-center" data-id="element-69">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" data-id="element-70">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-71"></path>
              <polyline points="7 10 12 15 17 10" data-id="element-72"></polyline>
              <line x1="12" y1="15" x2="12" y2="3" data-id="element-73"></line>
            </svg>
            Export
          </button>
        </div>
      </div>
    </div>;
};